import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';
import { fetchConfig } from '@theme/utilities';

/**
 * After a successful Horizon product-form add, posts a second cart line for the
 * merchant-selected travel product when the checkbox is checked. Does not modify
 * product-form.js.
 */
class SybilTravelAddon extends HTMLElement {
  connectedCallback() {
    if (this.abortController) return;
    this.abortController = new AbortController();
    const { signal } = this.abortController;

    this.addEventListener('change', this.handleChange, { signal });
    document.addEventListener(StandardEvents.cartLinesUpdate, this.handleCartLinesUpdate, {
      signal,
    });
  }

  disconnectedCallback() {
    this.abortController?.abort();
    this.abortController = undefined;
  }

  get checkbox() {
    return this.querySelector('[data-sybil-travel-addon-input]');
  }

  handleChange = () => {
    const input = this.checkbox;
    if (!(input instanceof HTMLInputElement)) return;
    this.classList.toggle('is-checked', input.checked);
    this.setAttribute('aria-checked', input.checked ? 'true' : 'false');
  };

  handleCartLinesUpdate = (event) => {
    if (!(event instanceof CartLinesUpdateEvent)) return;
    if (event.action !== 'add') return;
    if (this.dataset.sybilAddonPending === 'true') return;
    if (event.detail?.source === 'sybil-travel-addon') return;
    // Only follow main product-form adds (not cart/dialog/standard-action).
    if (event.context !== 'product') return;
    // Only follow the product form this addon belongs to — quick-add forms
    // elsewhere on the page also dispatch with context 'product'.
    if (!this.#isOwnerEvent(event)) return;

    const input = this.checkbox;
    const variantId = this.dataset.variantId;
    if (!(input instanceof HTMLInputElement) || !input.checked || !variantId) return;
    if (input.disabled) return;

    event.promise
      .then((result) => {
        if (result?.detail?.didError) return;
        if (result?.detail?.source === 'sybil-travel-addon') return;
        return this.addSecondLine(variantId);
      })
      .catch(() => {
        /* Main add failed or rejected; do not add the travel line. */
      });
  };

  /**
   * True when the event was dispatched by the product form this addon belongs
   * to: the form whose id Liquid passed via data-form-id, falling back to any
   * form inside the same section as this block.
   */
  #isOwnerEvent(event) {
    const target = event.target;
    if (!(target instanceof Element)) return false;

    const formId = this.dataset.formId;
    const ownerForm = formId ? document.getElementById(formId) : null;
    if (ownerForm) return target === ownerForm || target.contains(ownerForm);

    const section = this.closest('.shopify-section');
    return Boolean(section && section.contains(target));
  }

  async addSecondLine(variantId) {
    this.dataset.sybilAddonPending = 'true';

    let alreadyInCart = false;
    try {
      const cartResponse = await fetch(`${Theme.routes.cart_url}.json`, { ...fetchConfig() });
      const cart = await cartResponse.json();
      alreadyInCart =
        Array.isArray(cart.items) &&
        cart.items.some((item) => String(item.variant_id) === String(variantId));
    } catch {
      /* Cart lookup failed — fall through and attempt the add. */
    }

    if (alreadyInCart) {
      this.dataset.sybilAddonPending = 'false';
      return;
    }

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionIds = [];
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionIds.push(item.dataset.sectionId);
      }
    });

    const deferredEventPromise = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: [{ merchandiseId: String(variantId), quantity: 1 }],
        promise: deferredEventPromise.promise,
        detail: { source: 'sybil-travel-addon' },
      }),
    );

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          items: [{ id: Number(variantId), quantity: 1 }],
          sections: sectionIds.join(','),
        }),
      });
      const payload = await response.json();

      if (payload.status) {
        deferredEventPromise.resolve({
          cart: null,
          detail: {
            didError: true,
            items: [],
            source: 'sybil-travel-addon',
            sourceId: this.id || this.dataset.blockId || '',
            itemCount: 1,
            productId: this.dataset.productId,
          },
        });
        return;
      }

      const cartResponse = await fetch(`${Theme.routes.cart_url}.json`, {
        ...fetchConfig(),
      });
      const ajaxCart = await cartResponse.json();

      deferredEventPromise.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
        detail: {
          didError: false,
          items: ajaxCart.items,
          source: 'sybil-travel-addon',
          sourceId: this.id || this.dataset.blockId || '',
          itemCount: 1,
          productId: this.dataset.productId,
          sections: payload.sections,
        },
      });
    } catch (error) {
      deferredEventPromise.reject(error);
    } finally {
      this.dataset.sybilAddonPending = 'false';
    }
  }
}

if (!customElements.get('sybil-travel-addon')) {
  customElements.define('sybil-travel-addon', SybilTravelAddon);
}
